import React, { useState } from 'react';
import { login, resetPasswordWithMfa } from '../services/api';

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryTotpCode, setRecoveryTotpCode] = useState('');
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRecoveryPassword, setShowRecoveryPassword] = useState(false);
  const [showRecoveryConfirmPassword, setShowRecoveryConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');

    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError('Ingresa tu correo corporativo.');
      return;
    }
    if (!password) {
      setError('Ingresa tu contraseña.');
      return;
    }
    if (mfaRequired && totpCode.length !== 6) {
      setError('Ingresa el código de 6 dígitos de tu authenticator.');
      return;
    }

    setLoading(true);
    try {
      const data = await login(cleanEmail, password, mfaRequired ? totpCode : '');
      onLoginSuccess(data.usuario);
    } catch (err) {
      if (err.message === 'MFA_REQUIRED') {
        setMfaRequired(true);
        setError('Ingresa el código de 6 dígitos de tu authenticator.');
      } else {
        setError(err.message || 'Credenciales incorrectas. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecovery = async (event) => {
    event.preventDefault();
    setRecoveryMessage('');
    setError('');

    const cleanRecoveryEmail = recoveryEmail.trim();
    if (!cleanRecoveryEmail) {
      setRecoveryMessage('Ingresa el correo corporativo para recuperar el acceso.');
      return;
    }
    if (recoveryTotpCode.length !== 6) {
      setRecoveryMessage('Ingresa el código de 6 dígitos de tu authenticator.');
      return;
    }
    if (recoveryPassword.length < 12) {
      setRecoveryMessage('La nueva contraseña debe tener al menos 12 caracteres.');
      return;
    }
    if (recoveryPassword !== recoveryConfirmPassword) {
      setRecoveryMessage('La confirmación no coincide con la nueva contraseña.');
      return;
    }

    setRecoveryLoading(true);
    try {
      const data = await resetPasswordWithMfa(cleanRecoveryEmail, recoveryTotpCode, recoveryPassword);
      setRecoveryMessage(data.message || 'Contraseña actualizada. Ya puedes ingresar con tu nueva clave.');
      setRecoveryTotpCode('');
      setRecoveryPassword('');
      setRecoveryConfirmPassword('');
    } catch (err) {
      setRecoveryMessage(err.message || 'No se pudo recuperar el acceso.');
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="gv-login">
      <div className="gv-login__content gv-fade-in">
        <div className="gv-login__brand">
          <img
            src="/logo-gv-azul-color.png"
            alt="GasValpo + energías"
            className="gv-login__logo"
          />
          <p className="gv-login__subtitle">
            GasValpo OT Cobranza
          </p>
        </div>

        <div className="gv-card gv-login__card">
          <h2>Iniciar Sesión</h2>
          <p className="gv-login__hint">
            Ingresa con tu cuenta corporativa GasValpo
          </p>

          {error && (
            <div className="gv-alert gv-alert--error" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <form noValidate onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div className="gv-form-group">
              <label className="gv-label" htmlFor="login-email">Correo corporativo</label>
              <input
                id="login-email"
                type="email"
                className="gv-input"
                placeholder="usuario@gasvalpo.cl"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                required
              />
            </div>

            <div className="gv-form-group">
              <label className="gv-label" htmlFor="login-password">Contraseña</label>
              <PasswordField
                inputId="login-password"
                value={password}
                onChange={setPassword}
                visible={showPassword}
                onToggle={() => setShowPassword((current) => !current)}
                autoComplete="current-password"
                placeholder="************"
              />
            </div>

            {mfaRequired && (
              <div className="gv-form-group">
                <label className="gv-label" htmlFor="login-totp">Código authenticator</label>
                <input
                  id="login-totp"
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  className="gv-input"
                  placeholder="123456"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  required
                />
              </div>
            )}

            <button
              type="submit"
              className="gv-btn gv-btn--primary gv-btn--full gv-btn--lg"
              disabled={loading}
              style={{ marginTop: '8px' }}
            >
              {loading ? 'Verificando...' : (mfaRequired ? 'Validar código e ingresar' : 'Ingresar al Sistema')}
            </button>
          </form>

          <button
            type="button"
            className="gv-link-button"
            onClick={() => {
              setShowRecovery((current) => !current);
              setRecoveryMessage('');
            }}
          >
            Olvidé mi contraseña
          </button>

          {showRecovery && (
            <form noValidate onSubmit={handleRecovery} className="password-recovery">
              <div className="gv-form-group">
                <label className="gv-label">Correo corporativo</label>
                <input
                  type="email"
                  className="gv-input"
                  placeholder="usuario@gasvalpo.cl"
                  value={recoveryEmail}
                  onChange={(event) => setRecoveryEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Código authenticator</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  className="gv-input"
                  placeholder="123456"
                  value={recoveryTotpCode}
                  onChange={(event) => setRecoveryTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Nueva contraseña</label>
                <PasswordField
                  value={recoveryPassword}
                  onChange={setRecoveryPassword}
                  visible={showRecoveryPassword}
                  onToggle={() => setShowRecoveryPassword((current) => !current)}
                  autoComplete="new-password"
                  placeholder="Mínimo 12 caracteres"
                />
              </div>
              <div className="gv-form-group">
                <label className="gv-label">Confirmar nueva contraseña</label>
                <PasswordField
                  value={recoveryConfirmPassword}
                  onChange={setRecoveryConfirmPassword}
                  visible={showRecoveryConfirmPassword}
                  onToggle={() => setShowRecoveryConfirmPassword((current) => !current)}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                className="gv-btn gv-btn--outline gv-btn--full"
                disabled={recoveryLoading}
              >
                {recoveryLoading ? 'Validando...' : 'Cambiar contraseña'}
              </button>
              {recoveryMessage && (
                <div className={`gv-alert ${recoveryMessage.startsWith('Contraseña actualizada') ? 'gv-alert--success' : 'gv-alert--warning'}`}>
                  {recoveryMessage}
                </div>
              )}
            </form>
          )}
        </div>

        <p className="gv-login__help">
          ¿Problemas para acceder? Contacta al administrador del sistema.
        </p>
      </div>
    </div>
  );
};

const PasswordField = ({ inputId, value, onChange, visible, onToggle, autoComplete, placeholder = '' }) => (
  <div className="password-input">
    <input
      id={inputId}
      type={visible ? 'text' : 'password'}
      className="gv-input password-input__field"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      autoComplete={autoComplete}
      required
    />
    <button
      type="button"
      className="password-input__toggle"
      onClick={onToggle}
      aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
    >
      {visible ? <EyeOffIcon /> : <EyeIcon />}
    </button>
  </div>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M2.4 12s3.5-6 9.6-6 9.6 6 9.6 6-3.5 6-9.6 6-9.6-6-9.6-6Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M3 3l18 18" />
    <path d="M2.4 12s3.5-6 9.6-6c1.3 0 2.5.3 3.5.7" />
    <path d="M21.6 12s-3.5 6-9.6 6c-1.3 0-2.5-.3-3.6-.7" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

export default Login;
